/**
 * ABComparator — compara 2 test runs y muestra summary + diff.
 *
 * Endpoints:
 *   GET /api/qa/test-runs?limit=50          → { runs }   (filtra completed para selectors)
 *   GET /api/qa/ab-compare?runA=X&runB=Y    → { runA, runB, summary, diff }
 *
 * summary: { common_cases, same_match, a_pass, b_pass, a_only_pass, b_only_pass, avg_score_delta }
 * diff item: { golden_id, product_name, a:{match,score,pass,...}, b:{...}, same_match, score_delta, pass_delta }
 */
import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config';
import './ABComparator.css';

function fmtDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch (_) { return '—'; }
}

function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  return (
    <div className={`um-toast ${toast.type === 'error' ? 'um-toast-error' : ''}`}>
      {toast.message}
    </div>
  );
}

function pct(num, den) {
  if (!den) return '—';
  return `${((Number(num) / Number(den)) * 100).toFixed(1)}%`;
}

function Delta({ value, suffix = '' }) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return <span className="qa-delta-zero">0{suffix}</span>;
  const cls = n > 0 ? 'qa-delta-pos' : 'qa-delta-neg';
  const sign = n > 0 ? '+' : '';
  return <span className={cls}>{sign}{n.toFixed ? n.toFixed(1) : n}{suffix}</span>;
}

export default function ABComparator() {
  const [runs,    setRuns]    = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [runA,    setRunA]    = useState('');
  const [runB,    setRunB]    = useState('');
  const [data,    setData]    = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState(null);
  const [toast,   setToast]   = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const fetchRuns = useCallback(async () => {
    setLoadingRuns(true);
    try {
      const r = await fetch(`${API_BASE}/api/qa/test-runs?limit=50`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      const list = (Array.isArray(j.runs) ? j.runs : []).filter(
        x => x.status === 'completed' || x.status === 'error' || x.status === 'cancelled'
      );
      setRuns(list);
    } catch (e) {
      showToast(e.message || 'Error al cargar runs', 'error');
    } finally {
      setLoadingRuns(false);
    }
  }, [showToast]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const handleCompare = async () => {
    if (!runA || !runB) {
      setError('Seleccioná runA y runB.');
      return;
    }
    if (runA === runB) {
      setError('Elegí dos runs diferentes.');
      return;
    }
    setBusy(true);
    setError(null);
    setData(null);
    try {
      const url = `${API_BASE}/api/qa/ab-compare?runA=${encodeURIComponent(runA)}&runB=${encodeURIComponent(runB)}`;
      const res = await fetch(url);
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      const j = await res.json();
      setData(j);
    } catch (e) {
      setError(e.message || 'Error al comparar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="um-card">
        <div className="qa-ab-toolbar">
          <div className="qa-ab-selector">
            <label>Run A</label>
            <select
              value={runA}
              onChange={(e) => setRunA(e.target.value)}
              disabled={loadingRuns}
            >
              <option value="">— elegir run —</option>
              {runs.map(r => (
                <option key={r.id} value={r.id}>
                  #{r.id} · {r.run_name || '(sin nombre)'} · {fmtDateTime(r.ran_at)}
                </option>
              ))}
            </select>
          </div>
          <div className="qa-ab-selector">
            <label>Run B</label>
            <select
              value={runB}
              onChange={(e) => setRunB(e.target.value)}
              disabled={loadingRuns}
            >
              <option value="">— elegir run —</option>
              {runs.map(r => (
                <option key={r.id} value={r.id}>
                  #{r.id} · {r.run_name || '(sin nombre)'} · {fmtDateTime(r.ran_at)}
                </option>
              ))}
            </select>
          </div>
          <button
            className="um-btn um-btn-primary"
            onClick={handleCompare}
            disabled={busy || !runA || !runB}
            style={{ marginBottom: 1 }}
          >
            {busy ? 'Comparando...' : 'Comparar'}
          </button>
          <button
            className="um-btn um-btn-ghost"
            onClick={fetchRuns}
            disabled={loadingRuns}
            style={{ marginBottom: 1 }}
          >
            Refrescar runs
          </button>
        </div>

        {error && <div className="um-error" style={{ margin: 14 }}>{error}</div>}

        {!data && !error && (
          <div className="qa-ab-empty">
            {loadingRuns
              ? 'Cargando runs...'
              : runs.length === 0
                ? 'No hay runs completados todavía.'
                : 'Elegí dos runs para comparar.'}
          </div>
        )}

        {data && (
          <>
            <div className="qa-ab-summary">
              <div className="qa-ab-kpi">
                <div className="qa-ab-kpi-label">Casos comunes</div>
                <div className="qa-ab-kpi-value">{data.summary?.common_cases ?? 0}</div>
                <div className="qa-ab-kpi-sub">golden_items en ambos runs</div>
              </div>
              <div className="qa-ab-kpi">
                <div className="qa-ab-kpi-label">Same match</div>
                <div className="qa-ab-kpi-value">
                  {pct(data.summary?.same_match, data.summary?.common_cases)}
                </div>
                <div className="qa-ab-kpi-sub">{data.summary?.same_match ?? 0} casos coinciden</div>
              </div>
              <div className="qa-ab-kpi">
                <div className="qa-ab-kpi-label">Solo A pass</div>
                <div className="qa-ab-kpi-value qa-delta-neg">
                  {data.summary?.a_only_pass ?? 0}
                </div>
                <div className="qa-ab-kpi-sub">regresiones de B vs A</div>
              </div>
              <div className="qa-ab-kpi">
                <div className="qa-ab-kpi-label">Solo B pass</div>
                <div className="qa-ab-kpi-value qa-delta-pos">
                  {data.summary?.b_only_pass ?? 0}
                </div>
                <div className="qa-ab-kpi-sub">mejoras de B vs A</div>
              </div>
              <div className="qa-ab-kpi">
                <div className="qa-ab-kpi-label">Avg score Δ (B−A)</div>
                <div className="qa-ab-kpi-value">
                  <Delta value={data.summary?.avg_score_delta} />
                </div>
                <div className="qa-ab-kpi-sub">delta promedio de score</div>
              </div>
            </div>

            <div className="um-table-wrap">
              {Array.isArray(data.diff) && data.diff.length === 0 ? (
                <div className="um-empty">Sin casos comunes para comparar.</div>
              ) : (
                <table className="qa-ab-table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>A · match</th>
                      <th>A · pass</th>
                      <th>B · match</th>
                      <th>B · pass</th>
                      <th>Same?</th>
                      <th>Δ score</th>
                      <th>Δ pass</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.diff || []).map(d => {
                      const cls = d.b.pass && !d.a.pass
                        ? 'row-improve'
                        : (d.a.pass && !d.b.pass ? 'row-regress' : '');
                      return (
                        <tr key={d.golden_id} className={cls}>
                          <td title={d.product_name}>{d.product_name || '—'}</td>
                          <td className="qa-ab-cell-side">
                            <div className="qa-ab-match">{d.a.match || '—'}</div>
                            <div className="qa-ab-score">{d.a.score != null ? Number(d.a.score).toFixed(1) : '—'}</div>
                          </td>
                          <td>
                            {d.a.pass
                              ? <span className="qa-pass-yes">PASS</span>
                              : <span className="qa-pass-no">FAIL</span>}
                          </td>
                          <td className="qa-ab-cell-side">
                            <div className="qa-ab-match">{d.b.match || '—'}</div>
                            <div className="qa-ab-score">{d.b.score != null ? Number(d.b.score).toFixed(1) : '—'}</div>
                          </td>
                          <td>
                            {d.b.pass
                              ? <span className="qa-pass-yes">PASS</span>
                              : <span className="qa-pass-no">FAIL</span>}
                          </td>
                          <td>
                            {d.same_match
                              ? <span className="qa-ab-tick">✓</span>
                              : <span className="qa-ab-cross">✗</span>}
                          </td>
                          <td><Delta value={d.score_delta} /></td>
                          <td><Delta value={d.pass_delta} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
}
